/**
 * Pure query-shape helpers for the Algolia-backed calibre search.
 *
 * Sibling to `brandSearchQuery.ts` (pickers) and `brandListQuery.ts` (listing),
 * and kept out of `algolia.ts` for the same reason those are: the parameter
 * building and the local-fallback matching can then be unit-tested without
 * pulling in the Algolia client — and the `import.meta.env` credential read that
 * happens at its module scope.
 */

import { normalizeCalibreKey } from './calibreResolution';

/**
 * Attributes a calibre hit carries.
 *
 * Every one is emitted by `buildCalibreIndexRecord` in watchlock
 * (`functions/src/entityIndexes.ts`) — verified against that builder rather than
 * assumed, because requesting an attribute the index doesn't hold is silently
 * dropped rather than an error, so this list is only as good as the indexer it
 * mirrors.
 *
 * `allManufacturerIds` is the denormalised set: primary `manufacturerId`, every
 * `additionalManufacturerIds` co-developer, and each one's heritage ancestors.
 * The raw co-developer array IS also indexed now (watchlock #865) but is left
 * off this projection deliberately: the pickers this serves hydrate before they
 * decide anything, and the listing asks for it through its own wider projection
 * (`CALIBRE_LIST_ATTRIBUTES`).
 */
export const CALIBRE_SEARCH_ATTRIBUTES = [
  'objectID',
  'name',
  'alsoKnownAs',
  'brandId',
  'brandName',
  'manufacturerId',
  'allManufacturerIds',
  'movementType',
  'isInHouse',
  'referenceCount',
  'familyId',
] as const;

/**
 * Fields the admin's calibre surfaces render or decide on that the index does
 * NOT carry — which is why every hit is hydrated back to its Firestore document
 * before a picker renders it, and why several surfaces still load the collection
 * outright.
 *
 * Documented rather than merely implied, because each one is a silent wrong
 * answer if a future caller assumes the hit is the whole calibre:
 *
 * - `aliasDetails` / `isCatchAll` — brand-scoped-AKA and junk-bucket rules. The
 *   index can NARROW candidates by name but can never decide a match (see
 *   `calibreEntryMatchesRef`).
 * - `images` — the ARRAY. The record carries one pre-resolved `imageUrl` for a
 *   thumbnail and a `hasImage` boolean, never the entries themselves, so
 *   anything that REWRITES the array (`bulkSetCalibreImageSource`) must be
 *   handed documents.
 * - `data.tiers` and the rest of `CalibreData` — tier pickers, the effective-spec
 *   preview, and everything the edit form writes back. Only four specs
 *   (`movementType`, `isInHouse`, `jewels`, `powerReserveHours`) are flattened
 *   onto the record; notably `frequencyVph` is not, only the derived
 *   `isHighBeat` boolean.
 *
 * Not on this list any more, as of watchlock #865: `deprecated`, `source`,
 * `createdAt` and `additionalManufacturerIds` ARE indexed now, which is what
 * made the calibre LISTING expressible in Algolia (see `calibreListQuery.ts`)
 * the way watchlock #863 did for the brands listing. They stay absent from
 * `CALIBRE_SEARCH_ATTRIBUTES` only because the pickers hydrate anyway.
 */
export const CALIBRE_FIELDS_NOT_INDEXED = [
  'aliasDetails',
  'isCatchAll',
  'images',
  'data',
] as const;

/**
 * Filters the calibre search can apply.
 *
 * Every one maps to an attribute declared in `attributesForFaceting` on the live
 * index (`pushAlgoliaCalibreIndexSettings`, watchlock). Requesting a filter on a
 * NON-faceted attribute 400s the whole search, so this set must only ever grow
 * alongside the index config, which lives server-side.
 *
 * Deliberately absent: `tier`. The admin's tier model lives in `data.tiers` on
 * the Firestore document and is not on the Algolia record at all, so a `tier`
 * filter could only ever be a silent no-op or a 400. Tier-aware surfaces
 * hydrate and filter locally.
 *
 * Deliberately absent: `brandName`. It IS on the record but is not faceted, so
 * legacy calibres carrying a free-text `brandName` and no `brandId` cannot be
 * brand-scoped server-side — see `buildCalibreSearchParams`.
 */
export interface CalibreSearchFilters {
  /** Canonical brand FK. */
  brandId?: string;
  /** Calibre family membership (`calibre_families/{id}`). */
  familyId?: string;
  /**
   * Manufacturer ids to match against `allManufacturerIds` — ORed together, so
   * this reads as "made by, co-developed by, or descended from ANY of these".
   */
  manufacturerIds?: string[];
  movementType?: string;
  isInHouse?: boolean;
}

/**
 * Algolia search parameters for one page of calibre results.
 *
 * `brandId` and `manufacturerIds` share ONE facet-filter group, so they OR
 * rather than AND: a calibre belongs to a brand-scoped pick list if it carries
 * the brand's id OR was made by a manufacturer tied to that brand (the
 * co-developer / in-house case `calibreTiedToBrandViaManufacturers` covers).
 * Every other filter is its own group, so groups AND together.
 *
 * NOTE the brand-scope OR cannot include the legacy free-text `brandName`: it is
 * on the record but not faceted. A calibre with `brandName: "Panerai"` and no
 * `brandId` therefore does NOT appear under a brand-scoped pick list, though it
 * is still findable by typing its name and under an unscoped ("All brands")
 * view. Adding `filterOnly(brandName)` in watchlock would close that gap.
 *
 * `removeWordsIfNoResults: 'allOptional'` mirrors `searchCalibreIdsByName`: a
 * typed calibre string is often brand-prefixed ("Sellita SW200-1") while the
 * record is not, or vice versa, and an AND over every word would return nothing.
 *
 * `attributesToHighlight: []` because no calibre surface renders highlight
 * markup. `analytics: false` keeps admin browsing out of the app's search
 * analytics. `cacheable: false` bypasses the SDK's in-memory response cache, so
 * a calibre renamed in the editor is findable by its new name without a reload.
 */
export function buildCalibreSearchParams(
  filters: CalibreSearchFilters,
  page: number,
  hitsPerPage: number,
  opts?: { attributes?: readonly string[]; facets?: readonly string[] },
): Record<string, unknown> {
  const facetFilters: string[][] = [];

  const brandScope: string[] = [];
  if (filters.brandId) brandScope.push(`brandId:${filters.brandId}`);
  for (const id of filters.manufacturerIds ?? []) {
    if (id) brandScope.push(`allManufacturerIds:${id}`);
  }
  if (brandScope.length > 0) facetFilters.push(brandScope);

  if (filters.familyId) facetFilters.push([`familyId:${filters.familyId}`]);
  if (filters.movementType) {
    facetFilters.push([`movementType:${filters.movementType}`]);
  }
  if (filters.isInHouse !== undefined) {
    facetFilters.push([`isInHouse:${filters.isInHouse}`]);
  }

  const params: Record<string, unknown> = {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    removeWordsIfNoResults: 'allOptional',
    attributesToRetrieve: [...(opts?.attributes ?? CALIBRE_SEARCH_ATTRIBUTES)],
    attributesToHighlight: [],
  };
  if (facetFilters.length > 0) params.facetFilters = facetFilters;
  if (opts?.facets && opts.facets.length > 0) {
    params.facets = [...opts.facets];
    params.maxValuesPerFacet = 1000;
  }
  return params;
}

/** The shape every calibre-ish list item in the admin shares. */
export interface CalibreSearchable {
  id: string;
  name?: string;
  alsoKnownAs?: string[];
}

/**
 * The pre-Algolia substring match, kept as the offline fallback.
 *
 * Covers what the pickers checked on their own — canonical name and every
 * `alsoKnownAs` alias — through `normalizeCalibreKey`, so "caliber" and
 * "calibre" keep searching interchangeably when Algolia is unreachable.
 */
export function localCalibreMatches(
  calibre: CalibreSearchable,
  term: string,
): boolean {
  const needle = normalizeCalibreKey(term);
  if (!needle) return true;
  if (normalizeCalibreKey(calibre.name ?? '').includes(needle)) return true;
  return (calibre.alsoKnownAs ?? []).some((alias) =>
    normalizeCalibreKey(alias).includes(needle),
  );
}

/**
 * Resolve Algolia hit ids back to the caller's own calibre objects.
 *
 * The counterpart to the hydrating `useCalibreSearch` flow, for the surface that
 * ALREADY holds every calibre — the listing, which loads the whole index up
 * front. There Algolia only has to decide WHICH calibres match and in what
 * order; re-reading them would be pure cost.
 *
 * Mirrors `mergeBrandSearchResults`, and for the same two reasons:
 *
 * - A hit with no local object is DROPPED. The caller renders and acts on its
 *   own rows, so a hit it cannot resolve would be a dead row.
 * - Local matches Algolia did NOT return are APPENDED after the ranked hits. The
 *   index is eventually consistent, so a calibre created seconds ago must not
 *   become unfindable; ranked matches still come first.
 *
 * `hitIds` of `null` means "no Algolia answer" (unconfigured, failed, or still
 * in flight) — the result is then the pure local filter.
 *
 * `matches` defaults to {@link localCalibreMatches} (name + aliases). The
 * listing passes its own, because its search box has always matched the brand
 * column too and the fallback must not quietly match LESS than the box did
 * before Algolia.
 */
export function mergeCalibreSearchResults<T extends CalibreSearchable>(
  items: T[],
  hitIds: string[] | null,
  term: string,
  matches: (item: T, term: string) => boolean = localCalibreMatches,
): T[] {
  if (!term.trim()) return items;
  const byId = new Map<string, T>();
  for (const item of items) byId.set(item.id, item);

  const merged: T[] = [];
  const seen = new Set<string>();
  for (const id of hitIds ?? []) {
    const item = byId.get(id);
    if (item && !seen.has(id)) {
      merged.push(item);
      seen.add(id);
    }
  }
  for (const item of items) {
    if (!seen.has(item.id) && matches(item, term)) {
      merged.push(item);
      seen.add(item.id);
    }
  }
  return merged;
}

/**
 * Restore Algolia's ranking over a set of hydrated documents.
 *
 * The hydration pass reads the hit ids in parallel and skips the ones that fail
 * or no longer exist, so it returns neither the original order nor necessarily
 * the same length. Ranking is the whole reason the search ran, so it is
 * reapplied here rather than left to the caller.
 *
 * Documents whose id is not in `hitIds` are dropped: they can only come from a
 * caller mixing sources, and a picker row the search did not return would be
 * unexplainable to the admin.
 */
export function orderByHitIds<T extends { id: string }>(
  docs: T[],
  hitIds: string[],
): T[] {
  const byId = new Map(docs.map((d) => [d.id, d]));
  const ordered: T[] = [];
  for (const id of hitIds) {
    const doc = byId.get(id);
    if (doc) ordered.push(doc);
  }
  return ordered;
}
